import * as Icons from "@nova/component-icons"
import { SIGNAL_QUALITY_LABELS, MemberType } from "@nova/constants"
import { Card, MetricRow } from "../../MetricLayout"
import { AlertContainer } from "@nova/component-alert-container"
import { MissionQuerySingleResult } from "../MissionPageQueries"
import { useParams } from "react-router"
import { useMemo } from "react"
import { useRouter } from "pages"
import classNames from "classnames"
import { LinkText } from "components/LinkText"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { getCompleteChecklistModalComponent } from "features/pilot-tracking/flights-page/CompleteChecklistModal"
import { AssignPilotDropdown } from "features/pilot-tracking/flights-page/AssignPilotDropdown"
import { AssignDroneDropdown } from "features/pilot-tracking/flights-page/AssignDroneDropdown"
import { useCustomFieldDefinition } from "features/pilot-tracking/missions/linked-layers/CustomFieldsQueries"
import { useMissionCustomFields } from "features/pilot-tracking/missions/linked-layers/MissionCustomFieldsQueries"
import { CUSTOM_FIELDS_SECTION_ID } from "features/pilot-tracking/missions/linked-layers/CustomFieldsCard"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"

export const SummaryCard = ({ data }: { data: MissionQuerySingleResult }) => {
    const { missionId = "" } = useParams()
    const router = useRouter("/workspace/:workspaceId")
    const { isEditor } = useWorkspacePermissions()
    const users = useAppSelector(selectAllRegisteredMembers)
    const members = useMemo(() => users.filter((u) => u.type === MemberType.Member) ?? [], [users])

    const {
        "aircraft.id": aircraftId,
        "aircraft.nickname": aircraftNickname,
        "aircraft.model": aircraftModel,
        user_id: userId,
        "metadata.location_name": locationName,
        "metadata.takeoff_latitude": takeoffLatitude,
        "metadata.takeoff_longitude": takeoffLongitude,
        "metadata.avg_signal_quality": avgSignalQuality,
        "metadata.started_at": startedAt,
        "metadata.duration": duration,
        "metadata.max_battery_percent": maxBattery,
        "metadata.min_battery_percent": minBattery,
        checklistSubmissions,
    } = data

    const flightStart = startedAt
    const flightEnd =
        startedAt && duration != null ? new Date(startedAt.getTime() + duration * 1000) : null

    const pilotName = useMemo(
        () => members.find((u) => u.id === userId)?.fullName ?? null,
        [members, userId]
    )

    const hasSubmittedChecklist = !!+checklistSubmissions
    const takeoffLocation =
        takeoffLatitude != null && takeoffLongitude != null
            ? `${takeoffLatitude.toFixed(4)}°, ${takeoffLongitude.toFixed(4)}°`
            : "--"

    const { data: customFieldDefinition } = useCustomFieldDefinition()
    const { data: missionCustomFields } = useMissionCustomFields({ missionId })
    const hasMissingResponses = (customFieldDefinition?.form ?? []).some(
        (field) =>
            !(missionCustomFields?.answers ?? []).some(
                (a) => a.id === field.id && a.value !== "" && a.value !== undefined
            )
    )

    const aircraftName = aircraftNickname ?? aircraftModel
    const missingDetails = !hasSubmittedChecklist || !aircraftId || hasMissingResponses

    const modal = useConfirmationModalContext()

    return (
        <Card className="flex flex-col gap-5 basis-96 bg-white-100">
            <small className="text-gray-50 xs">Summary</small>

            {missingDetails && (
                <div className="flex flex-col gap-2.5">
                    {!hasSubmittedChecklist && (
                        <div
                            className="cursor-pointer"
                            onClick={() => {
                                void modal.confirm({
                                    RenderComponent: getCompleteChecklistModalComponent({
                                        missionIds: [missionId],
                                    }),
                                })
                            }}
                        >
                            <AlertContainer
                                type="warning"
                                body={
                                    <div className="w-full flex items-center justify-between text-white">
                                        <small>Missing Checklist</small>
                                        <Icons.SmallIcon Icon={Icons.ArrowRight} />
                                    </div>
                                }
                                className="!flex-row !items-center"
                            />
                        </div>
                    )}
                    {hasMissingResponses && (
                        <div
                            className="cursor-pointer"
                            onClick={() =>
                                document
                                    .getElementById(CUSTOM_FIELDS_SECTION_ID)
                                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                            }
                        >
                            <AlertContainer
                                type="warning"
                                body={
                                    <div className="w-full flex items-center justify-between text-white">
                                        <small>Missing Responses</small>
                                        <Icons.SmallIcon
                                            Icon={Icons.ArrowRight}
                                            className="rotate-90"
                                        />
                                    </div>
                                }
                                className="!flex-row !items-center"
                            />
                        </div>
                    )}
                    {!aircraftId && (
                        <AssignDroneDropdown
                            missionIds={[missionId]}
                            hasMixedDroneAssignment={false}
                            defaultCheckedDroneId={aircraftId}
                            trigger={
                                <div
                                    className={classNames("w-full", {
                                        "cursor-pointer": isEditor,
                                    })}
                                >
                                    <AlertContainer
                                        type="warning"
                                        body={
                                            <div className="w-full flex items-center justify-between text-white">
                                                <small>Missing Drone</small>
                                                {isEditor && (
                                                    <Icons.SmallIcon Icon={Icons.ArrowRight} />
                                                )}
                                            </div>
                                        }
                                        className="!flex-row !items-center"
                                    />
                                </div>
                            }
                            subtitle="Apply a drone to this flight."
                            disabled={!isEditor}
                        />
                    )}
                </div>
            )}

            <div className="w-full h-px bg-gray-700" />

            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between w-full gap-2">
                    <AssignPilotDropdown
                        missionIds={[missionId]}
                        hasMixedPilotAssignment={false}
                        defaultCheckedPilotId={userId}
                        trigger={
                            <div
                                className={classNames(
                                    "flex gap-3 items-center shrink-0",
                                    isEditor && "group cursor-pointer"
                                )}
                            >
                                <Icons.Icon
                                    className="text-gray-400 group-hover:text-white transition-colors"
                                    Icon={Icons.Controller}
                                />
                                <h5 className="text-gray-400 group-hover:text-white transition-colors">
                                    Pilot in Command
                                </h5>
                                {isEditor && (
                                    <Icons.SmallIcon
                                        Icon={Icons.Edit}
                                        className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                    />
                                )}
                            </div>
                        }
                        subtitle="Apply a pilot to this flight."
                        disabled={!isEditor}
                    />
                    {userId && pilotName ? (
                        <router.Link
                            to={{
                                path: "/fleet/pilots/:pilotId",
                                params: { pilotId: userId },
                            }}
                        >
                            <h5 className="text-white shrink min-w-0 truncate">
                                <LinkText>{pilotName}</LinkText>
                            </h5>
                        </router.Link>
                    ) : (
                        <h5 className="text-gray-0 shrink min-w-0 truncate">--</h5>
                    )}
                </div>

                <div className="flex items-center justify-between w-full gap-2">
                    <AssignDroneDropdown
                        missionIds={[missionId]}
                        hasMixedDroneAssignment={false}
                        defaultCheckedDroneId={aircraftId}
                        trigger={
                            <div
                                className={classNames(
                                    "flex gap-3 items-center shrink-0",
                                    isEditor && "group cursor-pointer"
                                )}
                            >
                                <Icons.Icon
                                    className="text-gray-400 group-hover:text-white transition-colors"
                                    Icon={Icons.Drone}
                                />
                                <h5 className="text-gray-400 group-hover:text-white transition-colors">
                                    Drone
                                </h5>
                                {isEditor && (
                                    <Icons.SmallIcon
                                        Icon={Icons.Edit}
                                        className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                    />
                                )}
                            </div>
                        }
                        subtitle="Apply a drone to this flight."
                        disabled={!isEditor}
                    />
                    {aircraftName && aircraftId ? (
                        <router.Link
                            to={{
                                path: "/fleet/aircraft/:aircraftId",
                                params: { aircraftId },
                            }}
                        >
                            <h5 className="text-white shrink min-w-0 truncate">
                                <LinkText>{aircraftName}</LinkText>
                            </h5>
                        </router.Link>
                    ) : (
                        <h5 className="text-gray-0 shrink min-w-0 truncate">--</h5>
                    )}
                </div>

                <MetricRow
                    title="Time"
                    value={`${format24Hour(flightStart)} - ${format24Hour(flightEnd)}`}
                    Icon={Icons.Schedule}
                />
                <MetricRow
                    title="Battery"
                    value={`${maxBattery?.toFixed(0) ?? "--"}% → ${minBattery?.toFixed(0) ?? "--"}%`}
                    Icon={Icons.Battery4Bar}
                />
            </div>

            <div className="w-full h-px bg-gray-700" />

            <div className="flex flex-col gap-2">
                <MetricRow
                    title="Signal Quality"
                    value={SIGNAL_QUALITY_LABELS[Math.round(avgSignalQuality ?? 0)] ?? "--"}
                    Icon={Icons.AndroidWifiBar}
                />
                <MetricRow title="Location" value={locationName ?? "--"} Icon={Icons.LocationOn} />
                <MetricRow
                    title="Takeoff Location"
                    value={takeoffLocation}
                    Icon={Icons.PDFMapPosition}
                />
            </div>
        </Card>
    )
}

const format24Hour = (date: Date | null) => {
    if (!date) return "--"
    return date.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
    })
}
